import {
  collection,
  deleteDoc,
  doc,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { COL, type MediaAsset } from '@eprintsmart/shared';
import { auth, db, storage } from '../firebase';

export type MediaStatus = 'PROCESSING' | 'READY' | 'FAILED';

export interface MediaDoc extends Partial<MediaAsset> {
  id: string;
  status: MediaStatus;
  error?: string;
  originalFilename: string;
  alt: string;
  tags: string[];
  createdAt: number;
}

/** Accepted on upload. HEIC is common from iPhones and sharp handles it. */
export const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
  'image/heic',
  'image/heif',
];

export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/**
 * Live docs for exactly these ids.
 *
 * Why not resolve attached images out of subscribeToMedia: that query is a
 * newest-200 window, and the library is well past 2,600 docs. Every product
 * whose photos are older than the 200 most recent then resolves to nothing and
 * is reported as "no longer exists in the library" — which was true of the
 * entire catalogue. Resolving by id is exact regardless of library size.
 *
 * One onSnapshot per id rather than a batched `in` query: no 30-item cap, no
 * composite index, and each doc's PROCESSING -> READY flip arrives on its own
 * listener, which is what the upload flow waits on. Attached sets are single
 * digits, so the listener count is not a concern.
 */
export function subscribeToMediaByIds(
  ids: string[],
  onChange: (byId: Map<string, MediaDoc>) => void,
): () => void {
  const unique = [...new Set(ids.filter(Boolean))];
  const byId = new Map<string, MediaDoc>();

  if (unique.length === 0) {
    onChange(byId);
    return () => {};
  }

  const unsubs = unique.map((id) =>
    onSnapshot(
      doc(db, COL.media, id),
      (snap) => {
        // A missing doc is a real state (someone deleted it), not an error —
        // leaving it out of the map is exactly how the caller detects that.
        if (snap.exists()) byId.set(id, { id: snap.id, ...snap.data() } as MediaDoc);
        else byId.delete(id);
        onChange(new Map(byId));
      },
      () => {
        byId.delete(id);
        onChange(new Map(byId));
      },
    ),
  );

  return () => unsubs.forEach((u) => u());
}

export function subscribeToMedia(
  onChange: (items: MediaDoc[]) => void,
  options: { tag?: string; limit?: number } = {},
) {
  const constraints = [
    ...(options.tag ? [where('tags', 'array-contains', options.tag)] : []),
    orderBy('createdAt', 'desc'),
    fsLimit(options.limit ?? 200),
  ];

  return onSnapshot(query(collection(db, COL.media), ...constraints), (snap) => {
    onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as MediaDoc));
  });
}

export interface UploadHandle {
  id: string;
  filename: string;
  /** 0–100 for the byte transfer; processing happens after this reaches 100. */
  progress: number;
  error?: string;
}

/**
 * Creates the media document first, then uploads.
 *
 * Doing it in that order means the console has something to render immediately
 * and the storage trigger has a document to update when it finishes — no race,
 * and a failed upload leaves a visible FAILED tile rather than an orphan file.
 */
export async function uploadMedia(
  file: File,
  meta: { alt?: string; tags?: string[] },
  onProgress?: (progress: number) => void,
): Promise<string> {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    throw new Error(`${file.name}: unsupported type ${file.type || 'unknown'}.`);
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is 25 MB.`);
  }

  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('You are not signed in.');

  const id = doc(collection(db, COL.media)).id;

  await setDoc(doc(db, COL.media, id), {
    id,
    status: 'PROCESSING',
    originalFilename: file.name,
    // Falling back to the filename gives a usable, if imperfect, alt attribute
    // rather than an empty one — better for accessibility and for SEO.
    alt: meta.alt?.trim() || filenameToAlt(file.name),
    tags: meta.tags ?? [],
    isPlaceholder: false,
    bytes: file.size,
    contentType: file.type,
    uploadedBy: uid,
    createdAt: Date.now(),
  });

  const storageRef = ref(storage, `media/originals/${id}/${sanitizeFilename(file.name)}`);
  const task = uploadBytesResumable(storageRef, file, { contentType: file.type });

  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => onProgress?.((snap.bytesTransferred / snap.totalBytes) * 100),
      reject,
      () => resolve(),
    );
  });

  // The document is completed by the onMediaUpload trigger; the caller is
  // already subscribed and will see it flip to READY.
  await getDownloadURL(task.snapshot.ref).catch(() => undefined);
  return id;
}

export async function updateMediaMeta(id: string, patch: { alt?: string; tags?: string[] }) {
  await updateDoc(doc(db, COL.media, id), { ...patch, updatedAt: Date.now() });
}

/**
 * Removes the media document. The Storage objects are swept separately —
 * deleting them here would break any product still referencing the image,
 * and an unreferenced file costs a fraction of a paisa a month.
 */
export async function deleteMedia(id: string) {
  await deleteDoc(doc(db, COL.media, id));
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-120);
}

function filenameToAlt(name: string): string {
  return name
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Best available URL for a given display size. */
export function mediaUrl(item: MediaDoc, size: 'thumb' | 'md' | 'lg' = 'thumb'): string | undefined {
  return item.variants?.[size] ?? item.variants?.md ?? item.url;
}
